import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import useTheme from '@mui/material/styles/useTheme'
import Typography from '@mui/material/Typography'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'

import { i18WithParams as t } from '@shared/locale'
import { imageResizer } from 'components-ui/src/shared/image_resizer'
import { Types } from 'service-api'

interface TPetPhotoFormProps {
  onFileUpload: (files: File) => void
  lpp?: Types.LocationPetProfileClientDto
}

export const PetPhotoForm = ({ lpp, onFileUpload }: TPetPhotoFormProps) => {
  const theme = useTheme()
  const [image, setImage] = useState<string | null>(null)

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles || typeof acceptedFiles.length === 'undefined' || acceptedFiles.length === 0) {
        return
      }
      const file = acceptedFiles[0]
      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setImage(reader.result as string)
        }
        reader.readAsDataURL(file)
        onFileUpload(file)
      }
    },
    [onFileUpload]
  )

  const onDropRejected = useCallback(() => {
    enqueueSnackbar(t('detailsPolicies.vaccinationRecords.upload.error'), {
      variant: 'error',
    })
  }, [])

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
      'image/gif': [],
    },
    noClick: true,
    maxSize: 9000000,
    multiple: false,
  })

  const profileImage = lpp?.profileImage?.[0]?.asset
  const imageWidth = 222
  const imageHeight = 148
  const uploadedImage = image ? `url(${image})` : undefined

  return (
    <>
      <a id="photo" />
      <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
        {t('profile.pet.form.petPhoto')}
      </Typography>
      <Box
        {...getRootProps()}
        sx={{
          alignItems: 'center',
          backgroundColor: theme.palette.grey[100],
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor: theme.palette.grey[400],
          display: 'flex',
          height: imageHeight,
          width: imageWidth,
          justifyContent: 'center',
          backgroundImage:
            profileImage && !image
              ? `url(${imageResizer({
                  bucket: profileImage?.bucket,
                  key: profileImage?.key,
                  edits: { resize: { height: imageHeight, width: imageWidth, fit: 'cover' } },
                })})`
              : uploadedImage,
          backgroundSize: image && 'cover',
        }}
      >
        <input {...getInputProps()} />

        <Stack spacing={2} alignItems="center">
          <Button variant="contained" onClick={open} size="small">
            {t('profile.pet.form.petPhoto.button')}
          </Button>
        </Stack>
      </Box>
    </>
  )
}
